import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { KibRadio } from '@chewy/kib-choices-react'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibInputGroup } from '@chewy/kib-fields-react'
import { KibTopLevelAlert } from '@chewy/kib-messaging-react'
import { KibModal } from '@chewy/kib-overlays-react'
import classNames from 'classnames'
import * as R from 'ramda'
import { LanguageUtils, type Nil, NumberUtils } from '@pbt/pbt-ui-components'

import type { RetailOrderLineItem } from '~/api/graphql/generated/types'
import { getIsRetailOrderLineItem } from '~/components/dashboard/invoices/invoiceUtils'
import NumberField from '~/components/dashboard/invoices/payment/add-payment-dialog-v2/components/NumberField'
import { MIN_QUANTITY } from '~/components/dashboard/soapV2/charges/constants'
import type { InvoiceLineItem } from '~/types'

import '@chewy/kib-choices-styles/dist/styles/kib-radio.css'
import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import '@chewy/kib-fields-styles/dist/styles/kib-input-group.css'
import '@chewy/kib-messaging-styles/dist/styles/kib-alert-icon.css'
import '@chewy/kib-messaging-styles/dist/styles/kib-top-level-alert.css'
import '@chewy/kib-overlays-styles/dist/styles/kib-modal.css'
import styles from './ApplyManualDiscountModal.module.scss'

type DiscountType = 'price' | 'percentage'

// Helper function to determine initial discount type from item
const getInitialDiscountType = (
  item: InvoiceLineItem | RetailOrderLineItem | Nil,
): DiscountType => {
  if (!item) return 'price'

  const isRetailOrder = getIsRetailOrderLineItem(item)
  if (isRetailOrder) return 'price'

  const invoiceItem = item as InvoiceLineItem

  // Prioritize discountPerc if it exists and is greater than 0
  if (invoiceItem.discountPerc != null && invoiceItem.discountPerc > 0) {
    return 'percentage'
  }

  // Otherwise default to price (dollar)
  return 'price'
}

export interface ApplyManualDiscountModalProps {
  item: InvoiceLineItem | RetailOrderLineItem | Nil
  onApplyDiscount: (
    discountAmount: number,
    quantity: number,
    discountPercentage?: number,
  ) => void
  onClose: () => void
  open: boolean
}

const ApplyManualDiscountModal = ({
  item,
  onApplyDiscount,
  onClose,
  open,
}: ApplyManualDiscountModalProps) => {
  const { t } = useTranslation(['Common', 'Payments', 'Validations'])

  // Local state for discount inputs
  const [discountType, setDiscountType] = useState<DiscountType>(() =>
    getInitialDiscountType(item),
  )
  const [discountAmount, setDiscountAmount] = useState(() => {
    if (!item) return ''
    const isRetailOrder = getIsRetailOrderLineItem(item)
    if (isRetailOrder) return String(item.discountAmount || '')

    const invoiceItem = item as InvoiceLineItem
    // If percentage mode, show percentage value (multiply by 100 to display as %)
    if (
      invoiceItem.discountPerc != null &&
      invoiceItem.discountPerc > 0 &&
      (invoiceItem.discountAmount == null || invoiceItem.discountAmount === 0)
    ) {
      return String(invoiceItem.discountPerc * 100)
    }
    // Otherwise show amount value
    return String(invoiceItem.discountAmount || '')
  })
  const [quantity, setQuantity] = useState(
    item ? String(item.quantity ?? 1) : '1',
  )

  // Reset state when item changes
  useEffect(() => {
    if (item) {
      const newDiscountType = getInitialDiscountType(item)
      setDiscountType(newDiscountType)

      const isRetailOrder = getIsRetailOrderLineItem(item)
      if (isRetailOrder) {
        setDiscountAmount(String(item.discountAmount || ''))
      } else {
        const invoiceItem = item as InvoiceLineItem
        // If percentage mode, show percentage value (multiply by 100 to display as %)
        if (newDiscountType === 'percentage') {
          setDiscountAmount(String((invoiceItem.discountPerc || 0) * 100))
        } else {
          setDiscountAmount(String(invoiceItem.discountAmount || ''))
        }
      }

      setQuantity(String(item.quantity ?? 1))
    }
  }, [item])

  if (!item) {
    return null
  }

  const itemName =
    LanguageUtils.getTranslatedFieldName(item) || t('Common:ITEM')

  // Get current values
  const originalQuantity = Math.max(MIN_QUANTITY, item.quantity ?? 1)
  const currentSubTotal = item.subTotal ?? 0
  const parsedQuantity = parseFloat(quantity)
  // Minimum quantity is 0.01 - fall back to minimum if invalid or too small
  const itemQuantity =
    Number.isNaN(parsedQuantity) || parsedQuantity < MIN_QUANTITY
      ? Math.max(MIN_QUANTITY, originalQuantity)
      : parsedQuantity
  const discountValue = Number.isNaN(parseFloat(discountAmount))
    ? 0
    : parseFloat(discountAmount)

  // Calculate the original price BEFORE any discount was applied
  const calculateOriginalSubTotal = () => {
    if (getIsRetailOrderLineItem(item)) {
      return currentSubTotal
    }

    const invoiceItem = item as InvoiceLineItem

    // If there's a discount amount, add it back to get original
    if (invoiceItem.discountAmount != null && invoiceItem.discountAmount > 0) {
      return currentSubTotal + invoiceItem.discountAmount
    }

    // If there's a discount percentage, reverse calculate the original
    if (invoiceItem.discountPerc != null && invoiceItem.discountPerc > 0) {
      // currentSubTotal = originalPrice * (1 - discountPerc)
      // originalPrice = currentSubTotal / (1 - discountPerc)
      // Guard against 100% discount (discountPerc = 1.0) to prevent division by zero
      if (invoiceItem.discountPerc >= 1) {
        // If 100% discount, the current subtotal is 0, so we can't reverse calculate
        // Return the current subtotal as best estimate
        return currentSubTotal
      }
      return currentSubTotal / (1 - invoiceItem.discountPerc)
    }

    // No discount, current is original
    return currentSubTotal
  }

  const originalSubTotalBeforeDiscount = calculateOriginalSubTotal()

  // Calculate unit price from original (pre-discount) price
  // originalQuantity is guaranteed to be at least MIN_QUANTITY (0.01)
  // Guard against zero/negative originalQuantity edge case
  const unitPrice =
    originalQuantity > 0 ? originalSubTotalBeforeDiscount / originalQuantity : 0

  // Calculate what the total would be with new quantity (before discount)
  const estimatedSubTotal = unitPrice * itemQuantity

  // Original total for display (what it would be without discount)
  const originalTotal = estimatedSubTotal

  // Calculate discount amount based on type
  let actualDiscountAmount = 0

  if (discountType === 'percentage') {
    actualDiscountAmount = (estimatedSubTotal * discountValue) / 100
  } else {
    actualDiscountAmount = discountValue
  }

  // Updated total = estimated subtotal - discount amount
  const updatedTotal = Math.max(0, estimatedSubTotal - actualDiscountAmount)

  // Only show strikethrough if there's a discount
  const hasDiscount = actualDiscountAmount > 0

  // Validation logic - validates immediately on input change
  let validationError = null

  // Quantity validation
  if (!R.isNil(quantity) && !R.isEmpty(quantity)) {
    if (parsedQuantity < MIN_QUANTITY && !Number.isNaN(parsedQuantity)) {
      validationError = t('Validations:QUANTITY_MUST_BE_AT_LEAST_MIN', {
        min: MIN_QUANTITY,
      })
    }
  }

  // Discount validation - only if discount has a value and no quantity error
  if (
    !validationError &&
    !R.isNil(discountAmount) &&
    !R.isEmpty(discountAmount)
  ) {
    // Percentage validation
    if (discountType === 'percentage') {
      if (discountValue < 0 || discountValue > 100) {
        validationError = t('Validations:DISCOUNT_PERCENTAGE_INVALID')
      }
    }

    // Price amount validation
    if (discountType === 'price') {
      if (discountValue < 0 || discountValue > estimatedSubTotal) {
        validationError = t('Validations:DISCOUNT_AMOUNT_INVALID')
      }
    }
  }

  const handleCancel = () => {
    const isRetailOrder = getIsRetailOrderLineItem(item)
    if (!isRetailOrder) {
      const invoiceItem = item as InvoiceLineItem
      // Reset to original state based on which field is set
      if (
        invoiceItem.discountPerc != null &&
        invoiceItem.discountPerc > 0 &&
        (invoiceItem.discountAmount == null || invoiceItem.discountAmount === 0)
      ) {
        setDiscountType('percentage')
        setDiscountAmount(String(invoiceItem.discountPerc * 100))
      } else if (
        invoiceItem.discountAmount != null &&
        invoiceItem.discountAmount > 0 &&
        (invoiceItem.discountPerc == null || invoiceItem.discountPerc === 0)
      ) {
        setDiscountType('price')
        setDiscountAmount(String(invoiceItem.discountAmount))
      } else {
        setDiscountType('price')
        setDiscountAmount(String(invoiceItem.discountAmount || ''))
      }
    } else {
      setDiscountType('price')
      setDiscountAmount(String(item?.discountAmount || ''))
    }
    setQuantity(String(item?.quantity ?? 1))
    onClose()
  }

  const handleApply = () => {
    // When price is selected: pass discountAmount only (no percentage)
    // When percentage is selected: pass percentage only (set amount to 0)
    // Note: percentage must be divided by 100 to convert to decimal (e.g., 15% -> 0.15)
    if (discountType === 'percentage') {
      onApplyDiscount(0, itemQuantity, discountValue / 100)
    } else {
      onApplyDiscount(discountValue, itemQuantity, undefined)
    }
    handleCancel()
  }

  const handleDiscountTypeChange = (value: unknown) => {
    if (typeof value === 'string') {
      const newDiscountType = value as DiscountType
      const currentDiscountValue = parseFloat(discountAmount) || 0

      // Convert discount value when switching types
      if (currentDiscountValue > 0) {
        if (newDiscountType === 'percentage') {
          // Converting from dollar to percentage
          // Formula: (dollarAmount / originalPrice) * 100
          const percentageValue =
            originalSubTotalBeforeDiscount > 0
              ? (currentDiscountValue / originalSubTotalBeforeDiscount) * 100
              : 0
          setDiscountAmount(String(percentageValue.toFixed(2)))
        } else {
          // Converting from percentage to dollar
          // Formula: (originalPrice * percentage) / 100
          const dollarValue =
            (originalSubTotalBeforeDiscount * currentDiscountValue) / 100
          setDiscountAmount(String(dollarValue.toFixed(2)))
        }
      }

      setDiscountType(newDiscountType)
    }
  }

  const controls = () => (
    <div className={styles.controlsContainer}>
      <KibButtonNew
        className={styles.applyButton}
        disabled={
          R.isNil(discountAmount) ||
          R.isEmpty(discountAmount) ||
          Number.isNaN(discountValue) ||
          validationError !== null
        }
        emphasis="primary"
        onClick={handleApply}
      >
        {t('Payments:APPLY_DISCOUNT')}
      </KibButtonNew>
      <KibButtonNew
        className={styles.cancelButton}
        emphasis="secondary"
        onClick={handleCancel}
      >
        {t('Common:CANCEL_ACTION')}
      </KibButtonNew>
    </div>
  )

  return (
    <KibModal
      className={styles.modal}
      controls={controls}
      open={open}
      title={t('Payments:APPLY_MANUAL_DISCOUNT')}
      onClose={handleCancel}
    >
      <div className={styles.content}>
        <p className={styles.warningText}>
          {t('Payments:MANUAL_DISCOUNT_WARNING')}
        </p>

        {/* Validation Alert */}
        {validationError && (
          <KibTopLevelAlert heading={validationError} severity="error" />
        )}

        {/* Items table */}
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th className={styles.headerCell}>
                  {t('Payments:ITEM_TO_DISCOUNT')}
                </th>
                <th className={styles.headerCell}>{t('Common:QUANTITY')}</th>
                <th className={styles.headerCell}>{t('Common:DISCOUNT')}</th>
                <th className={styles.headerCell}>
                  {t('Payments:UPDATED_TOTAL')}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className={styles.dataRow}>
                <td className={styles.dataCell}>
                  <span className={styles.itemName}>{itemName}</span>
                </td>
                <td className={styles.dataCell}>
                  <NumberField
                    aria-label={t('Common:QUANTITY')}
                    className={styles.quantityInput}
                    label=""
                    min={0.01}
                    step="0.01"
                    value={quantity}
                    onChange={setQuantity}
                  />
                </td>
                <td className={styles.dataCell}>
                  <div className={styles.discountCell}>
                    <div className={styles.discountInputWrapper}>
                      {discountType === 'price' && (
                        <span className={styles.currencySymbol}>$</span>
                      )}
                      <NumberField
                        aria-label={t('Common:DISCOUNT_AMOUNT')}
                        className={classNames(
                          styles.discountInput,
                          discountType === 'price' && styles.withPrefix,
                          discountType === 'percentage' && styles.withSuffix,
                        )}
                        isMoney={discountType === 'price'}
                        label=""
                        min={0}
                        placeholder={
                          discountType === 'percentage' ? '0' : '0.00'
                        }
                        step="0.01"
                        value={discountAmount}
                        onChange={setDiscountAmount}
                      />
                      {discountType === 'percentage' && (
                        <span className={styles.percentSymbol}>%</span>
                      )}
                    </div>
                    <KibInputGroup
                      aria-label="discount-type-radio-group"
                      className={styles.radioGroup}
                    >
                      <KibRadio
                        label={t('Common:PERCENT')}
                        name="discount-type"
                        selectedValue={discountType}
                        value="percentage"
                        onChange={handleDiscountTypeChange}
                      />
                      <KibRadio
                        label={t('Common:DOLLAR')}
                        name="discount-type"
                        selectedValue={discountType}
                        value="price"
                        onChange={handleDiscountTypeChange}
                      />
                    </KibInputGroup>
                  </div>
                </td>
                <td className={styles.dataCell}>
                  <div className={styles.totalCellContent}>
                    <span className={styles.updatedPrice}>
                      {NumberUtils.formatMoney(updatedTotal)}
                    </span>
                    {hasDiscount && (
                      <span className={styles.originalPrice}>
                        {NumberUtils.formatMoney(originalTotal)}
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </KibModal>
  )
}

export default ApplyManualDiscountModal
